import { Component, Input, Optional, Self, ElementRef, ViewChild, Inject } from '@angular/core';
import { NgControl } from '@angular/forms';
import { TranslationService } from '../../../../services/translation.service';
import { TcmModalService } from '../../services/tcm-modal.service';
import { TcmAppSettings } from '../../../../common/tcm-app-settings';
import { SystemPreferences, SYSTEM_PREFERENCES } from '../../../../common/system-preferences';
import { FormControlBase } from '../../../../modules/tcm-form/common/form-control-base/form-control-base';

/**
 * Similar to @see TcmFileInputComponent but intended only for file uploading with following upload to the server as a stream.
 */
@Component({
    selector: 'tcm-file-upload',
    templateUrl: './tcm-file-upload.component.html',
    styleUrls: ['./tcm-file-upload.component.scss'],
    inputs: ['label', 'disabled', 'hideValidationMessages', 'isReadonlyValue', 'placeholder', 'tabindex'],
    standalone: false
})
export class TcmFileUploadComponent extends FormControlBase<File> {

  constructor(
    @Self() @Optional() protected override ngModel: NgControl,
    private modalService: TcmModalService,
    private translationService: TranslationService,
    @Inject(SYSTEM_PREFERENCES) private systemPreferences: SystemPreferences
  ) {
    super('tcm-file-upload', ngModel);      
    this.maxSizeMb =  this.systemPreferences.fileUploadSize;
    this.fileExtensions = this.systemPreferences.fileExtensions;
  }

  @ViewChild('fileInput')
  fileInput: ElementRef;

  @Input()
  acceptedTypes: string[];

  @Input()
  ignoreCase: boolean = false;

  @Input()
  maxSizeMb: number;

  @Input()
  allowEmpty: boolean = true;

  fileExtensions: string;

  onFilesChange($event: Event) {
    $event.stopPropagation();

    const fileInputElement = $event.target as HTMLInputElement;
    const file = fileInputElement.files[0];
    if (!file) {
      return;
    }

    let error = null;

    const allowedFileExtensions = this.fileExtensions.split(" ");    
    
    if (!this.acceptedTypes || this.acceptedTypes.length == 0) {
      this.acceptedTypes = allowedFileExtensions;
    }

    if (this.acceptedTypes && this.acceptedTypes.length > 0 && !this.acceptedTypes.some(x => x === '*')) {
      const fileExtension = file.name.substring(file.name.lastIndexOf('.'));

      if (!this.acceptedTypes.some(x => x === fileExtension || (this.ignoreCase && x.toLowerCase() === fileExtension.toLowerCase()))) {
        error = this.translationService.translate(x => x.Messages.ErrorMessages.FileTypeUnsupported).replace('{0}', fileExtension);
      }
    }

    const maxSizeBytes = this.maxSizeMb * 1024 * 1024;
    if (error == null && file.size > maxSizeBytes) {
      error = this.translationService.translate(x => x.Messages.ErrorMessages.FileSizeLimitReached)
        .replace('{0}', file.name)
        .replace('{1}', this.maxSizeMb);
    }

    if (!this.allowEmpty && this.acceptedTypes.includes(".csv") && file.size < 3) { //File less than 3 bytes (minimum for csv)
      error = this.translationService.translate(x => x.Messages.ErrorMessages.FileEmpty)
    }

    if (error != null) {
      $event.preventDefault();

      this.modalService.showErrorModalAsync(x => x.Messages.MessageHeadersAndActions.Error, error);

      return;
    }

    this.value = file;
  }

  public clearValue() {
    this.value = null;
    this.clearFileInput();
  }

  private clearFileInput() {
    this.fileInput.nativeElement.value = '';
  }

  override onValueWritten(previousValue: File, newValue: File) {
    super.onValueWritten(previousValue, newValue);

    if (!newValue) {
      // clear file input if value is set to null from outside (via ngModel binding)
      this.clearFileInput();
    }
  }
}
